import { isSameDay } from 'date-fns'
import DateDivider from 'features/Messaging/DateDivider'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import dashboardApi from 'services/api/dashboard'

import { getResolvedSalesChats, getThread } from '../../actions/messaging'
import CustomHeader from '../../components/ReusableComponents/HeaderTitles/index'
import LoadingIndicator from '../../components/ReusableComponents/LoadingIndicator'
import Chat from '../../components/ReusableComponents/Messaging/Chat/index'
import ConversationBubble from '../../components/ReusableComponents/Messaging/ConversationBubble/index'
import IntentSelector from '../../components/ReusableComponents/Messaging/IntentSelector/index'
import NoMessages from '../../components/ReusableComponents/Messaging/NoMessages'
import styles from './ChatBotTraining.module.scss'
import ChatTitle from './ChatTitle/index'
import Sidebar from './Sidebar/index'

function isStarter(m, i, arr) {
  const p = arr[i - 1]
  if (
    !p ||
    m.isSenderClient !== p.isSenderClient ||
    m.senderName !== p.senderName ||
    !isSameDay(new Date(m.scheduledOn), new Date(p.scheduledOn))
  ) {
    return true
  }

  return false
}

const ChatBotTraining = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(
      getResolvedSalesChats({
        take: '10',
        skip: '0',
        from: moment().add(-1, 'days').format('YYYY/MM/DD'),
        to: moment().format('YYYY/MM/DD'),
        contentCategories: [
          'AfterLastVisit',
          'CheckedOutReview',
          'Lost6Months',
          'Lost9Months',
          'Lost12Months',
          'Lost15Months'
        ]
      })
    )
  }, [dispatch])

  const resolvedChatsObj = useSelector(s => s.chatBotTraining.resolvedThreads)

  const resolvedSalesChats = Object.values(resolvedChatsObj)

  const [messageIdsToBeCategorized, setMessageIdsToBeCategorized] = useState([])
  const [intentSelectorPos, setIntentSelectorPos] = useState({ x: 0, y: 0 })
  const [chatIntentId, setChatIntentId] = useState()

  const [categorizedChatIds, setCategorizedChatIds] = useState({})

  const {
    params: { currentChatId }
  } = useRouteMatch({
    path: '/aiTraining/:currentChatId?'
  })

  useEffect(() => {
    if (currentChatId) {
      dispatch(getThread(currentChatId, 'service'))
    }
  }, [currentChatId, dispatch])

  function handleClickIntentButton(chatMessageId, e) {
    setMessageIdsToBeCategorized([...messageIdsToBeCategorized, chatMessageId])

    if (e.target.closest('button')) {
      const { left: x, top: y } = e.target.closest('button').getBoundingClientRect()
      setIntentSelectorPos({ x, y: Math.min(window.innerHeight - 140, y) })
    }
  }

  async function handleSelectChatIntent(intentId) {
    setChatIntentId(intentId)

    setCategorizedChatIds(s => ({ ...s, [currentChatId]: intentId }))

    await dashboardApi.putChatIntents({
      chatId: currentChatId,
      botContext: intentId,
      messages: []
    })
  }

  async function handleSelectMessageIntent(intentId) {
    await dashboardApi.putChatIntents({
      chatId: currentChatId,
      messages: messageIdsToBeCategorized.map(chatMessageId => ({
        chatMessageId,
        botContext: intentId
      }))
    })

    setMessageIdsToBeCategorized([])

    dispatch(getThread(currentChatId, 'service'))
  }

  function handleBubbleCheckboxChange(isChecked, chatMessageId) {
    if (isChecked) {
      setMessageIdsToBeCategorized([...messageIdsToBeCategorized, chatMessageId])
    } else {
      setMessageIdsToBeCategorized(
        messageIdsToBeCategorized.filter(id => id !== chatMessageId)
      )
    }
  }

  const currentThread = useSelector(s => s.serviceMessaging.threads[currentChatId])
  const isCurrentThreadLoading = useSelector(s => s.serviceMessaging.threadLoading)

  useEffect(() => {
    const botContext = currentThread && currentThread.botContext
    if (botContext) {
      setChatIntentId(botContext)
    } else {
      setChatIntentId(null)
    }
  }, [currentThread])

  useEffect(() => {
    setMessageIdsToBeCategorized([])
  }, [currentChatId])

  if (isCurrentThreadLoading === 0) {
    return <LoadingIndicator />
  }

  return (
    <>
      <CustomHeader title="Train AI" />
      <div className={styles.wrapper}>
        <Sidebar
          data={resolvedSalesChats}
          categorizedChatIds={categorizedChatIds}
          currentChatId={currentChatId}
        />
        {!currentThread && !isCurrentThreadLoading && (
          <div className={styles.chatBox}>
            <NoMessages />
          </div>
        )}
        {isCurrentThreadLoading && (
          <div className={styles.chatBox}>
            <LoadingIndicator />
          </div>
        )}
        {!isCurrentThreadLoading && currentThread && (
          <div className={styles.chatBox}>
            <ChatTitle
              intentId={chatIntentId}
              handleChangeIntent={handleSelectChatIntent}
            />
            {messageIdsToBeCategorized.length > 0 && (
              <IntentSelector
                {...intentSelectorPos}
                count={messageIdsToBeCategorized.length}
                handleClose={() => setMessageIdsToBeCategorized([])}
                handleSelect={handleSelectMessageIntent}
              />
            )}
            <div className={styles.chatBoxInner}>
              {messageIdsToBeCategorized.length > 0 && (
                <div
                  className={styles.overlay}
                  onClick={() => setMessageIdsToBeCategorized([])}
                ></div>
              )}
              <Chat noCompose>
                {currentThread.messages.reduce((pm, m, i, arr) => {
                  return [
                    ...pm,
                    <DateDivider
                      key={'d' + (m.chatMessageId || m.controlKey)}
                      prevMessage={arr[i - 1] || null}
                      currentMessage={m}
                    />,
                    <ConversationBubble
                      {...m}
                      starter={isStarter(m, i, arr)}
                      key={m.chatMessageId || m.controlKey}
                      isLastMessage={i + 1 === arr.length}
                      handleClickIntentButton={e =>
                        handleClickIntentButton(m.chatMessageId, e, arr, i)
                      }
                      intent={m.botContext}
                      highlighted={
                        messageIdsToBeCategorized.length > 0 && m.isSenderClient
                      }
                      checked={messageIdsToBeCategorized.includes(m.chatMessageId)}
                      handleCheckboxChange={e =>
                        handleBubbleCheckboxChange(e.target.checked, m.chatMessageId)
                      }
                      canCategorize
                      noInitials
                    >
                      {m.content}
                    </ConversationBubble>
                  ]
                }, [])}
              </Chat>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ChatBotTraining
